/**
 * SERA wishlist helpers (Growave SDK).
 *
 * Count badge — official Growave Storefront SDK:
 *   docs.growave.io → Sdk/guides/event_handling
 *   docs.growave.io → Sdk/guides/state_kit
 *   docs.growave.io → Sdk/modules/wishlists/methods/*
 *
 * Boot:
 *   document "gwSdkReady"
 *   → runIfModuleReady(WISHLISTS)
 *   → wishlists.methods.getWishlistsSharedStateKit()
 *   → stateKit.service.getWishlists().subscribe(...)
 *   → stateKit.query.selectAll().subscribe(...)
 *   → sdk.on(wishlists.events.WISHLIST_ITEM_CREATED|DELETED, refresh)
 *
 * WishlistSdkDto.totalCount (or items.length) is the source of truth.
 */

(() => {
  if (window.__seraWishlistInit) return;
  window.__seraWishlistInit = true;

  const ROOT_SELECTOR = '.sera-wishlist-card, .sera-wishlist-pdp';
  const FILLED_CLASS = 'sera-wishlist--filled';
  const COUNT_SELECTOR = '[data-sera-wishlist-count]';
  const COUNT_TEXT_SELECTOR = '[data-sera-wishlist-count-text]';
  const LEGACY_KEY = 'sera-wishlist-product-ids';

  const observed = new WeakSet();

  try {
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    // ignore
  }

  /**
   * @param {number} count
   */
  function setHeaderCount(count) {
    const safe = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
    const label = safe > 99 ? '99+' : String(safe);

    document.querySelectorAll(COUNT_SELECTOR).forEach((bubble) => {
      const text = bubble.querySelector(COUNT_TEXT_SELECTOR);
      if (text) {
        text.textContent = label;
        text.classList.toggle('hidden', safe <= 0);
      }

      if (safe > 0) {
        bubble.removeAttribute('hidden');
        bubble.classList.remove('visually-hidden');
        bubble.setAttribute('aria-hidden', 'false');
        bubble.classList.add('sera-wishlist-header__count--visible');
        if (safe <= 99) bubble.setAttribute('data-maintain-ratio', '');
        else bubble.removeAttribute('data-maintain-ratio');
      } else {
        bubble.setAttribute('hidden', '');
        bubble.setAttribute('aria-hidden', 'true');
        bubble.classList.remove('sera-wishlist-header__count--visible');
      }
    });
  }

  /**
   * @param {unknown} wishlists
   * @returns {number}
   */
  function countFromWishlists(wishlists) {
    if (!Array.isArray(wishlists)) return 0;

    return wishlists.reduce((sum, list) => {
      if (!list || typeof list !== 'object') return sum;
      const dto = /** @type {{totalCount?: number, items?: unknown[]}} */ (list);

      if (typeof dto.totalCount === 'number' && Number.isFinite(dto.totalCount)) {
        return sum + Math.max(0, dto.totalCount);
      }
      if (Array.isArray(dto.items)) {
        return sum + dto.items.length;
      }
      return sum;
    }, 0);
  }

  /**
   * @param {unknown} items
   * @returns {number}
   */
  function countFromItems(items) {
    return Array.isArray(items) ? items.length : 0;
  }

  /**
   * Pull count via methods.getWishlists / getWishlistItems (Promise API).
   */
  function refreshCountFromMethods() {
    const methods = window.gw?.sdk?.wishlists?.methods;
    if (!methods) return;

    if (typeof methods.getWishlists === 'function') {
      methods
        .getWishlists()
        .then((response) => {
          if (response?.result) setHeaderCount(countFromWishlists(response.result));
        })
        .catch(() => {
          if (typeof methods.getWishlistItems === 'function') {
            methods
              .getWishlistItems()
              .then((response) => {
                if (response?.result) setHeaderCount(countFromItems(response.result));
              })
              .catch(() => {});
          }
        });
      return;
    }

    if (typeof methods.getWishlistItems === 'function') {
      methods
        .getWishlistItems()
        .then((response) => {
          if (response?.result) setHeaderCount(countFromItems(response.result));
        })
        .catch(() => {});
    }
  }

  /**
   * @param {object} stateKit
   */
  function bindStateKit(stateKit) {
    if (!stateKit) return;

    // Reactive: update whenever shared wishlist state changes
    if (stateKit.query?.selectAll) {
      stateKit.query.selectAll().subscribe((wishlists) => {
        setHeaderCount(countFromWishlists(wishlists));
      });
    }

    // Initial fetch from server into shared state
    if (stateKit.service?.getWishlists) {
      stateKit.service.getWishlists().subscribe({
        next: (wishlists) => {
          setHeaderCount(countFromWishlists(wishlists));
        },
        error: () => {
          refreshCountFromMethods();
        },
      });
    } else {
      refreshCountFromMethods();
    }
  }

  function bindWishlistEvents() {
    const sdk = window.gw?.sdk;
    const events = sdk?.wishlists?.events;
    if (!sdk?.on || !events) return;

    const refresh = () => refreshCountFromMethods();

    if (events.WISHLIST_ITEM_CREATED) {
      sdk.on(events.WISHLIST_ITEM_CREATED, refresh);
    }
    if (events.WISHLIST_ITEM_DELETED) {
      sdk.on(events.WISHLIST_ITEM_DELETED, refresh);
    }
  }

  function bootWishlistCount() {
    const sdk = window.gw?.sdk;
    if (!sdk) return;

    const moduleName = sdk.common?.constants?.modules?.WISHLISTS;
    const start = () => {
      bindWishlistEvents();

      const methods = sdk.wishlists?.methods;
      if (!methods?.getWishlistsSharedStateKit) {
        refreshCountFromMethods();
        return;
      }

      methods
        .getWishlistsSharedStateKit()
        .then((response) => {
          if (response?.result) bindStateKit(response.result);
          else refreshCountFromMethods();
        })
        .catch(() => {
          refreshCountFromMethods();
        });
    };

    if (moduleName && typeof sdk.runIfModuleReady === 'function') {
      sdk.runIfModuleReady(moduleName, start);
    } else if (sdk.wishlists) {
      start();
    }
  }

  function onSdkReady() {
    bootWishlistCount();
  }

  if (window.gw?.sdk) onSdkReady();
  else document.addEventListener('gwSdkReady', onSdkReady, { once: true });

  // ——— Bookmark fill (visual only; count comes from SDK events/state) ———

  /**
   * @param {Element} root
   * @returns {boolean}
   */
  function isWishlisted(root) {
    if (
      root.querySelector(
        '[aria-pressed="true"], [aria-checked="true"], [data-added="true"], [data-gw-wishlist-added="true"], [data-wishlisted="true"], [data-state="added"], [data-state="active"]'
      )
    ) {
      return true;
    }

    for (const node of root.querySelectorAll('[class]')) {
      const className = typeof node.className === 'string' ? node.className : node.className?.baseVal || '';
      const lower = className.toLowerCase();
      if (
        lower.includes('wishlisted') ||
        lower.includes('is-added') ||
        lower.includes('in-wishlist') ||
        lower.includes('--added')
      ) {
        return true;
      }
    }

    for (const svg of root.querySelectorAll('svg')) {
      if (svg.closest('[class*="counter"], [class*="badge"], [class*="count"]')) continue;
      for (const shape of svg.querySelectorAll('path, polygon, circle, rect')) {
        const fillAttr = (shape.getAttribute('fill') || '').trim().toLowerCase();
        const fill = fillAttr || (getComputedStyle(shape).fill || '').toLowerCase();
        if (fill && fill !== 'none' && fill !== 'transparent' && fill !== 'rgba(0, 0, 0, 0)') {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * @param {Element} root
   */
  function syncRoot(root) {
    root.classList.toggle(FILLED_CLASS, isWishlisted(root));
  }

  /**
   * @param {Element} root
   */
  function observeRoot(root) {
    if (observed.has(root)) return;
    observed.add(root);

    syncRoot(root);

    const observer = new MutationObserver(() => syncRoot(root));
    observer.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: [
        'class',
        'aria-pressed',
        'aria-checked',
        'aria-selected',
        'data-added',
        'data-gw-wishlist-added',
        'data-wishlisted',
        'data-state',
        'fill',
      ],
    });

    // Fill feedback only — count is updated by Growave SDK events / state kit
    root.addEventListener(
      'click',
      (event) => {
        if (event.button != null && event.button !== 0) return;
        root.classList.toggle(FILLED_CLASS);
        requestAnimationFrame(() => syncRoot(root));
        setTimeout(() => syncRoot(root), 200);
        setTimeout(() => syncRoot(root), 800);
        // Safety refresh if an event was missed
        setTimeout(refreshCountFromMethods, 400);
        setTimeout(refreshCountFromMethods, 1200);
      },
      true
    );
  }

  function scan(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(observeRoot);
  }

  scan();

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches(ROOT_SELECTOR)) observeRoot(node);
        else scan(node);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', () => {
    scan();
    refreshCountFromMethods();
  });
})();
